'use client';
import Image from '@/src/components/Image';
import {
  Drawer,
  DrawerContent,
  DrawerTrigger,
  DrawerTitle,
  DrawerClose,
} from '@/src/components/ui/drawer';
import { Routes } from '@/src/routes/routes';
import { IBranchDetails } from '@/src/types/stores';
import { PRODUCT_IMAGE_SIZE } from '@/src/lib/utils/command';
import { Link } from '@/src/i18n/navigation';
import { ChevronDown, MapPin } from 'lucide-react';
import { useTranslations } from 'next-intl';
import Container from '@/src/components/ui/container';
import CurrentLanguageText from '@/src/components/helper/CurrentLanguageText';
import useStoreInfo from '@/src/lib/state-stores/storeInfoStore';
import { useState } from 'react';
import useNavigation from '@/src/lib/hooks/useNavigation';
import { trackEvent } from '@/src/lib/amplitude';
import { AmplitudeEvents } from '@/src/lib/amplitude/events';

type Props = {
  branch: IBranchDetails;
};
const BranchesList = ({ branch }: Props) => {
  const { setBranchId } = useStoreInfo();
  const [open, setOpen] = useState(false);
  const { createQueryString } = useNavigation();
  const t = useTranslations('stores');

  return (
    <Drawer open={open} onOpenChange={setOpen}>
      <DrawerTrigger className="w-full">
        <DrawerTitle>
          <div className="bg-secondary flex w-full items-center justify-between gap-2 rounded-full px-2 py-2 text-sm font-bold text-black lg:gap-3 lg:px-4">
            <p className="flex items-center gap-1 text-start text-xs lg:gap-2 lg:text-base">
              <MapPin className="size-4 shrink-0 lg:size-5" />
              <span className="line-clamp-1 transition-opacity group-hover:opacity-50">
                <CurrentLanguageText
                  arabicName={branch.arabicName}
                  englishName={branch.englishName}
                />
              </span>
              <span className="mx-0.5 shrink-0">
                {branch.distance} {t('km')}
              </span>

              {branch.availableBranches[0]?.isSelected && (
                <span className="text-primary hidden lg:block">({t('nearest')})</span>
              )}
            </p>
            <span className="px-1">
              <ChevronDown className="size-4 lg:size-5" />
            </span>
          </div>
        </DrawerTitle>
      </DrawerTrigger>

      <DrawerContent className="no-scrollbar max-h-[80dvh] min-h-[40vh] overflow-y-auto bg-white after:hidden">
        <Container>
          <DrawerTitle className="sticky top-0 mb-4 bg-white py-5 text-xl">
            {' '}
            {t('choose-branch')}
          </DrawerTitle>
          <div className="styled-scroll max-h-[80dvh] min-h-[40vh] overflow-y-auto pb-10">
            {branch.availableBranches?.map((item, index) => (
              <div
                key={index}
                onClick={async () => {
                  setBranchId(item.id.toString());
                  createQueryString({ bi: item.id.toString() });
                  trackEvent(AmplitudeEvents.branch_changed, {
                    shop_id: branch?.store.id,
                    shop_name: branch?.store.englishName,
                    old_branch_id: branch?.id,
                    old_branch_name: branch?.englishName,
                    new_branch_id: item?.id,
                    new_branch_name: item?.englishName,
                  });

                  setOpen(false);
                }}
                className={` ${item?.isSelected ? 'bg-primary/10' : ''} hover:bg-primary/10 mb-1 block w-full cursor-pointer overflow-hidden rounded-xl px-3`}
              >
                <div
                  className={`${item?.isSelected ? '' : 'border-b border-slate-200'} flex items-center gap-3 py-2`}
                >
                  <div className="h-12 w-12">
                    <Image
                      src={branch.store.logoUrl}
                      size={PRODUCT_IMAGE_SIZE.MEDIUM}
                      className="h-full w-full rounded-xl object-cover"
                      alt={item.englishName}
                      width={48}
                      height={48}
                    />
                  </div>
                  <div className="flex flex-col items-start justify-center">
                    <h2 className="text-sm font-semibold lg:text-base">
                      <CurrentLanguageText
                        arabicName={branch.store.arabicName}
                        englishName={branch.store.englishName}
                      />
                    </h2>
                    <p className="flex items-center justify-center gap-2 text-xs lg:text-base">
                      <span
                        className={` ${item.isOpen ? 'bg-green-600' : 'bg-red-600'} inline-block h-[10px] w-[10px] rounded-full`}
                      />

                      <span>
                        <CurrentLanguageText
                          arabicName={item.arabicName}
                          englishName={item.englishName}
                        />
                      </span>
                      <span className="centerButton text-gray-2 flex items-center gap-1">
                        <MapPin size={16} color="gray" /> {item.distance} {t('km')}
                      </span>
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </Container>
      </DrawerContent>
    </Drawer>
  );
};

export default BranchesList;
